import axios from 'axios';
import { tenantSlugFromPathOrHost } from '../utils/tenantDomains';
import { clearStoredSessionToken, getStoredSessionToken } from './sessionTokens';
import { resolveApiBaseUrl } from './runtimeConfig';

const getTenantSlug = () => {
  return tenantSlugFromPathOrHost();
};

const api = axios.create({
  baseURL: resolveApiBaseUrl(),
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  const token = getStoredSessionToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  const tenantSlug = getTenantSlug();
  if (tenantSlug) config.headers['X-Tenant-Slug'] = tenantSlug;
  // Auto-generate idempotency key for POST requests
  if (config.method === 'post' && !config.headers['X-Idempotency-Key']) {
    config.headers['X-Idempotency-Key'] = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Normalize error message: backend can return either {detail: "..."} or contract envelope {message: "...", code: "..."}
    if (error.response?.data) {
      const d = error.response.data;
      if (!d.detail && d.message) d.detail = d.message;
    }
    if (error.response?.status === 401) {
      const method = String(error.config?.method || '').toLowerCase();
      const isMutation = ['post', 'put', 'patch', 'delete'].includes(method);
      if (isMutation) {
        return Promise.reject(error);
      }
      const requestUrl = String(error.config?.url || '');
      const isAuthProbe = requestUrl.includes('/api/auth/me') || requestUrl.endsWith('/auth/me');
      if (!isAuthProbe) {
        return Promise.reject(error);
      }
      const token = getStoredSessionToken({ allowLegacyFallback: true });
      clearStoredSessionToken({ token });
      const path = window.location.pathname;
      const isProtected = path.startsWith('/app') || path.startsWith('/admin') || path.match(/^\/[^/]+\/(app|admin)/);
      if (isProtected) {
        const slug = getTenantSlug();
        const adminPath = path.startsWith('/admin') || Boolean(path.match(/^\/[^/]+\/admin/));
        if (adminPath) {
          window.location.href = slug ? `/${slug}/admin/auth` : '/admin/auth';
        } else {
          window.location.href = slug ? `/${slug}/auth` : '/auth';
        }
      }
    }
    return Promise.reject(error);
  }
);

export default api;
